import { App, AppState } from "@capacitor/app";
import { logSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import parseJwt from "src/lib/parseJwt";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { globalEvents } from "../../constants/globalEvents";
import type { AuthStage } from "../../types/auth";

type ParseResultUnknown =
  | string
  | number
  | boolean
  | Record<string | number | symbol, unknown>
  | Array<unknown>;

class MemStorage {
  storage: Map<string, string> = new Map();

  get length(): number {
    return this.storage.size;
  }

  getItem(key: string): string | null {
    return this.storage.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.storage.set(key, value);
  }

  clear(): void {
    this.storage.clear();
  }

  removeItem(key: string): void {
    this.storage.delete(key);
  }

  key(index: number): string | null {
    const keys = Array.from(this.storage.keys());
    return keys[index] ?? null;
  }
}

export const memStorage: Storage = new MemStorage();

export default class StorageBloc {
  useStorage: Storage = localStorage;
  sessionStorage = sessionStorage;
  pageIsActive = true;
  private _activeUserId: string | null = null;
  private _isInitialized = false;
  private _initializationPromise: Promise<void> | null = null;
  private _initializationResolve: (() => void) | null = null;

  get activeUserId(): string | null {
    return this._activeUserId;
  }

  get isInitialized(): boolean {
    return this._isInitialized;
  }

  setActiveUserId(userId: string | null): void {
    this.log("setActiveUserId", {
      previousUserId: this._activeUserId,
      newUserId: userId
    });
    this._activeUserId = userId;
  }

  constructor() {
    // Create initialization promise that will be resolved when storage is ready
    this._initializationPromise = new Promise<void>((resolve) => {
      this._initializationResolve = resolve;
    });

    window.addEventListener(globalEvents.USER_CLEAR, () => {
      this.clearUser();
    });
    void App.addListener("appStateChange", this.handleAppStateChange);
  }

  /**
   * Initialize the storage bloc with proper user context
   * This can be called early with automatic user detection from tokens
   */
  initialize(): void {
    if (this._isInitialized) {
      this.log("StorageBloc already initialized");
      return;
    }

    this.log("Initializing StorageBloc");

    // First, select storage type based on stored preference
    this.selectStorageType();

    // Try to find and set the best user from existing tokens
    if (!this._activeUserId) {
      const selectedUserId = this.selectAndActivateBestUser();
      if (selectedUserId) {
        this.log("Initialized with user from storage", {
          userId: selectedUserId
        });
      } else {
        this.log("No existing user found during initialization");
      }
    }

    this._isInitialized = true;

    // Resolve the initialization promise
    if (this._initializationResolve) {
      this._initializationResolve();
      this._initializationResolve = null;
    }
  }

  /**
   * Wait for storage to be initialized before performing operations
   */
  async waitForInitialization(): Promise<void> {
    if (this._isInitialized) {
      return;
    }
    if (this._initializationPromise) {
      await this._initializationPromise;
    }
  }

  /**
   * Auth stage priority for user selection
   * Higher priority stages are preferred when multiple users exist
   */
  private static readonly AUTH_STAGE_PRIORITY: AuthStage[] = [
    "fullAuth",
    "partialAuth",
    "anonymous",
    "noAuth",
    "nullAuth"
  ];

  /**
   * Select the best user from storage based on various criteria
   * Priority order:
   * 1. Users with app access (hasAppAccess = true)
   * 2. Users by auth stage (fullAuth > partialAuth > anonymous > noAuth > nullAuth)
   * 3. Most recently active user wins within each category
   * @returns The best user candidate with their storage location, or null if no users found
   */
  selectBestUser(): { userId: string; storage: Storage } | null {
    this.log("selectBestUser - starting search");

    const allKeys = [
      ...Object.keys(localStorage),
      ...Object.keys(sessionStorage)
    ];

    // Find all users with lastActive timestamps
    const lastActiveKeys = allKeys.filter((k) =>
      k.match(/^9am-[a-z0-9-]+-lastActive$/)
    );

    this.log("Found lastActive keys", {
      totalKeys: allKeys.length,
      lastActiveKeys: lastActiveKeys.length,
      keys: lastActiveKeys
    });

    // Track the best candidates
    const latestWithAccess = {
      userId: "",
      time: 0,
      storage: undefined as Storage | undefined
    };

    const latestByAuthStage: Record<
      AuthStage,
      { userId: string; time: number; storage?: Storage }
    > = {
      fullAuth: { userId: "", time: 0, storage: undefined },
      partialAuth: { userId: "", time: 0, storage: undefined },
      anonymous: { userId: "", time: 0, storage: undefined },
      noAuth: { userId: "", time: 0, storage: undefined },
      nullAuth: { userId: "", time: 0, storage: undefined }
    };

    // Process each user candidate
    for (const lastActiveKey of lastActiveKeys) {
      this.processUserCandidate(
        lastActiveKey,
        latestWithAccess,
        latestByAuthStage
      );
    }

    // Find the best candidate based on priorities
    const result = this.findBestUserCandidate(
      latestWithAccess,
      latestByAuthStage
    );

    this.log("selectBestUser - result", {
      result,
      latestWithAccess,
      latestByAuthStage
    });

    return result;
  }

  /**
   * Process a single user candidate for selection
   */
  private processUserCandidate(
    lastActiveKey: string,
    latestWithAccess: { userId: string; time: number; storage?: Storage },
    latestByAuthStage: Record<
      AuthStage,
      { userId: string; time: number; storage?: Storage }
    >
  ): void {
    const storage =
      localStorage.getItem(lastActiveKey) !== null
        ? localStorage
        : sessionStorage;
    const activeTime = parseInt(storage.getItem(lastActiveKey) ?? "0", 10);
    const match = lastActiveKey.match(/^9am-([a-z0-9-]+)-lastActive$/);
    const userId = match?.[1];

    this.log("Processing user candidate", {
      lastActiveKey,
      storage: storage === localStorage ? "localStorage" : "sessionStorage",
      activeTime,
      userId
    });

    if (!userId) {
      this.log("No userId found in key", { lastActiveKey });
      return;
    }

    // Check for refresh token in the same storage
    const hasRefreshToken = storage.getItem(`9am-${userId}-refreshToken`);
    if (!hasRefreshToken) {
      this.log("No refresh token for user in expected storage", {
        userId,
        expectedStorage:
          storage === localStorage ? "localStorage" : "sessionStorage"
      });
      return;
    }

    // Validate the refresh token
    if (!this.validateRefreshToken(hasRefreshToken, userId)) {
      this.log("Invalid refresh token for user", { userId });
      return;
    }

    // Check for app access
    const hasAppAccess =
      storage.getItem(`9am-${userId}-hasAppAccess`) === "true";

    this.log("User candidate evaluation", {
      userId,
      hasAppAccess,
      activeTime,
      storage: storage === localStorage ? "localStorage" : "sessionStorage"
    });

    if (hasAppAccess) {
      if (activeTime > latestWithAccess.time) {
        this.log("New best user with app access", {
          previousUserId: latestWithAccess.userId,
          newUserId: userId,
          previousTime: latestWithAccess.time,
          newTime: activeTime
        });
        latestWithAccess.userId = userId;
        latestWithAccess.time = activeTime;
        latestWithAccess.storage = storage;
      }
    } else {
      // Check for auth stage
      const authStage = storage.getItem(`9am-${userId}-authStage`);
      const category = this.categorizeAuthStage(authStage);

      this.log("User auth stage categorization", {
        userId,
        authStage,
        category,
        storage: storage === localStorage ? "localStorage" : "sessionStorage"
      });

      if (activeTime > latestByAuthStage[category].time) {
        this.log("New best user for auth stage", {
          category,
          previousUserId: latestByAuthStage[category].userId,
          newUserId: userId,
          previousTime: latestByAuthStage[category].time,
          newTime: activeTime
        });
        latestByAuthStage[category].userId = userId;
        latestByAuthStage[category].time = activeTime;
        latestByAuthStage[category].storage = storage;
      }
    }
  }

  /**
   * Validate a refresh token
   */
  private validateRefreshToken(token: string, userId: string): boolean {
    try {
      const parsedToken = parseJwt(token);
      if (!parsedToken.sub) {
        this.log("Invalid or missing JWT subject for user", { userId });
        return false;
      }
      return true;
    } catch (error) {
      this.log("Failed to parse JWT for user", { userId, error });
      return false;
    }
  }

  /**
   * Categorize an auth stage string into a valid AuthStage
   */
  private categorizeAuthStage(authStage: string | null): AuthStage {
    if (!authStage) return "nullAuth";

    const validStages: AuthStage[] = [
      "fullAuth",
      "partialAuth",
      "anonymous",
      "noAuth",
      "nullAuth"
    ];

    if (validStages.includes(authStage as AuthStage)) {
      return authStage as AuthStage;
    }

    return "nullAuth";
  }

  /**
   * Find the best user candidate based on priorities
   */
  private findBestUserCandidate(
    latestWithAccess: { userId: string; time: number; storage?: Storage },
    latestByAuthStage: Record<
      AuthStage,
      { userId: string; time: number; storage?: Storage }
    >
  ): { userId: string; storage: Storage } | null {
    // First priority: users with app access
    if (latestWithAccess.userId && latestWithAccess.storage) {
      return {
        userId: latestWithAccess.userId,
        storage: latestWithAccess.storage
      };
    }

    // Second priority: users by auth stage
    for (const stage of StorageBloc.AUTH_STAGE_PRIORITY) {
      if (latestByAuthStage[stage].userId && latestByAuthStage[stage].storage) {
        return {
          userId: latestByAuthStage[stage].userId,
          storage: latestByAuthStage[stage].storage
        };
      }
    }

    return null;
  }

  /**
   * Select and activate the best user from storage
   * This combines user selection with activation
   * @returns The selected user ID or null if no user found
   */
  selectAndActivateBestUser(): string | null {
    const bestUser = this.selectBestUser();

    if (bestUser) {
      this.log("Activating best user", bestUser);

      // Set the storage type to match the selected user
      this.switchStorageTo(bestUser.storage);

      // Set as active user
      this.setActiveUserId(bestUser.userId);

      // Log the activation
      this.logActive();

      return bestUser.userId;
    }

    this.log("No user found to activate");
    return null;
  }

  /**
   * Migrate temporary storage items to a specific user
   * This should be called after user authentication to move any
   * temporary data stored during signup/onboarding to the user's storage
   * Temporary items are always stored in sessionStorage, but are migrated
   * to the user's preferred storage type (this.useStorage)
   * @param userId - The user ID to migrate temporary storage to
   */
  migrateTemporaryStorageToUser(userId: string): void {
    this.log("Migrating temporary storage items to user", { userId });

    // Ensure we're initialized
    if (!this._isInitialized) {
      this.initialize();
    }

    // Set the user as active if not already
    if (this._activeUserId !== userId) {
      this.setActiveUserId(userId);
    }

    // Find all temporary storage keys in sessionStorage only
    const keys = Object.keys(sessionStorage);
    const tempKeys = keys.filter((key) => key.startsWith("9am-temp-"));

    if (tempKeys.length === 0) {
      this.log("No temporary storage items to migrate");
      return;
    }

    this.log("Found temporary storage items to migrate from sessionStorage", {
      count: tempKeys.length,
      keys: tempKeys,
      targetStorage:
        this.useStorage === localStorage ? "localStorage" : "sessionStorage"
    });

    for (const tempKey of tempKeys) {
      const value = sessionStorage.getItem(tempKey);

      if (value) {
        // Extract the actual key name (remove "9am-temp-" prefix)
        const actualKey = tempKey.replace("9am-temp-", "");

        try {
          // Store with user scope in the preferred storage type
          const fullKey = this.makeKey(actualKey);
          const userValue = this.useStorage.getItem(fullKey);

          sessionStorage.removeItem(tempKey);
          if (typeof userValue === "string") {
            this.log(
              "Skipping migration of temporary storage item - key already exists for user",
              {
                tempKey,
                actualKey,
                fullKey,
                userId,
                existingValueLength: userValue.length
              }
            );
            continue;
          }

          this.useStorage.setItem(fullKey, value);
        } catch (error) {
          this.log("Failed to migrate temporary storage item", {
            tempKey,
            actualKey,
            error
          });
          reportErrorSentry(
            new Error(`Failed to migrate temporary storage item, ${actualKey}`)
          );
        }
      }
    }
  }

  static keys = {
    refreshToken: "refreshToken",
    lastActive: "lastActive",
    useStorageType: "useStorageType",
    authStage: "authStage",
    hasAppAccess: "hasAppAccess"
  };

  createPatternForKey = (key: string): RegExp => {
    return new RegExp(`9am-([a-z0-9-]+)-${key}`);
  };

  getAllStoredUserIds(): string[] {
    const localStorageKeys = Object.keys(localStorage);
    const sessionStorageKeys = Object.keys(sessionStorage);
    const allStorageKeys = [...localStorageKeys, ...sessionStorageKeys];
    const userIdRegex = this.createPatternForKey("refreshToken");
    const userIds = new Set<string>();

    allStorageKeys.forEach((key) => {
      const match = key.match(userIdRegex);
      if (match && match[1]) {
        userIds.add(match[1]);
      }
    });

    return Array.from(userIds);
  }

  getItemFromBothStorages = (key: string, userId: string): string | null => {
    const localItem = localStorage.getItem(`9am-${userId}-${key}`);
    const sessionItem = sessionStorage.getItem(`9am-${userId}-${key}`);
    return localItem ?? sessionItem;
  };

  /**
   * Switches the active storage type and persists the preference.
   * @param storage - The storage type to switch to (localStorage or sessionStorage)
   */
  switchStorageTo = (storage: Storage): void => {
    const storageType = storage === localStorage ? "local" : "session";
    this.log("switchStorageTo", {
      previousStorage:
        this.useStorage === localStorage ? "localStorage" : "sessionStorage",
      newStorage: storageType === "local" ? "localStorage" : "sessionStorage"
    });
    this.sessionStorage.setItem(StorageBloc.keys.useStorageType, storageType);
    this.useStorage = storage;
  };

  /**
   * Selects the appropriate storage type based on stored preference.
   * Defaults to localStorage if no preference is set.
   */
  selectStorageType = (): void => {
    const storageType = this.sessionStorage.getItem(
      StorageBloc.keys.useStorageType
    );
    this.log("selectStorageType", {
      storedType: storageType,
      selectedStorage:
        storageType === "session" ? "sessionStorage" : "localStorage"
    });
    this.useStorage = storageType === "session" ? sessionStorage : localStorage;
  };

  handleAppStateChange = (state: AppState) => {
    const { isActive } = state;
    this.pageIsActive = isActive;
    if (isActive) {
      this.logActive();
    }
  };

  /**
   * Records the current timestamp as the last active time for the current user.
   * This is used to determine which user was most recently active.
   */
  logActive = (): void => {
    // Auto-initialize if not already done
    if (!this._isInitialized) {
      this.log("Auto-initializing storage on logActive call");
      this.initialize();
    }

    if (this.activeUserId) {
      this.setItem(StorageBloc.keys.lastActive, Date.now().toString());
    }
  };

  /**
   * Set the authentication stage for the active user
   * @param stage - The authentication stage
   */
  setAuthStage = (stage: AuthStage): void => {
    // Auto-initialize if not already done
    if (!this._isInitialized) {
      this.log("Auto-initializing storage on setAuthStage call", { stage });
      this.initialize();
    }

    if (this.activeUserId) {
      this.log("setAuthStage", {
        userId: this.activeUserId,
        stage
      });
      this.setItem(StorageBloc.keys.authStage, stage);
    } else {
      this.log("Cannot set auth stage - no active user", { stage });
      reportErrorSentry(new Error("No active user - cannot set auth stage"));
    }
  };

  /**
   * Get the authentication stage for the active user
   * @returns The authentication stage or null if not set
   */
  getAuthStage = (): AuthStage | null => {
    // Auto-initialize if not already done
    if (!this._isInitialized) {
      this.log("Auto-initializing storage on getAuthStage call");
      this.initialize();
    }

    if (!this.activeUserId) return null;
    const stage = this.getItem(StorageBloc.keys.authStage);

    const validStages: AuthStage[] = [
      "anonymous",
      "fullAuth",
      "noAuth",
      "partialAuth"
    ];
    if (validStages.includes(stage as AuthStage)) {
      return stage as AuthStage;
    }
    return null;
  };

  /**
   * Set whether the user has completed funnel/signup and has app access
   * @param hasAccess - Whether the user has app access
   */
  setHasAppAccess = (hasAccess: boolean): void => {
    // Auto-initialize if not already done
    if (!this._isInitialized) {
      this.log("Auto-initializing storage on setHasAppAccess call", {
        hasAccess
      });
      this.initialize();
    }

    if (this.activeUserId) {
      this.log("setHasAppAccess", {
        userId: this.activeUserId,
        hasAccess
      });
      this.setItem(StorageBloc.keys.hasAppAccess, hasAccess.toString());
    } else {
      this.log("Cannot set app access - no active user", { hasAccess });
    }
  };

  /**
   * Get whether the user has completed funnel/signup and has app access
   * @returns True if user has app access, false otherwise
   */
  getHasAppAccess = (): boolean => {
    // Auto-initialize if not already done
    if (!this._isInitialized) {
      this.log("Auto-initializing storage on getHasAppAccess call");
      this.initialize();
    }

    if (!this.activeUserId) return false;
    const access = this.getItem(StorageBloc.keys.hasAppAccess);
    return access === "true";
  };

  get loggingEnabled(): boolean {
    return false;
  }

  log = (message: string, etc?: unknown): void => {
    if (this.loggingEnabled) {
      // eslint-disable-next-line no-console
      console.warn(`[STORAGE]: ${message}`, etc ?? "");
    }
    logSentryBreadcrumb("storage", message);
  };

  makeKey = (key: string): string => {
    if (!this.activeUserId) {
      throw new Error(`No active user - cannot make key, ${key}`);
    }
    return `9am-${this.activeUserId.replace(/-/g, "")}-${key}`;
  };

  /**
   * Set an item without requiring a user ID - for signup/onboarding flows
   * These items are stored with a temporary key in sessionStorage and should be migrated after user creation
   */
  setItemNoUser = (key: string, value: string): void => {
    const tempKey = `9am-temp-${key}`;
    sessionStorage.setItem(tempKey, value);
  };

  /**
   * Get an item that was set without a user ID
   * Always checks sessionStorage for temporary pre-auth storage
   */
  getItemNoUser = (key: string): string | null => {
    const tempKey = `9am-temp-${key}`;
    const value = sessionStorage.getItem(tempKey);
    return value;
  };

  setItem = (key: string, value: string, storage = this.useStorage): void => {
    // Auto-initialize if not already done
    if (!this._isInitialized) {
      this.log("Auto-initializing storage on first setItem call", { key });
      this.initialize();
    }

    if (!this.activeUserId) {
      // Use temporary sessionStorage for pre-auth operations
      this.log("No active user - using temporary sessionStorage", { key });
      this.setItemNoUser(key, value);
      return;
    }
    const fullKey = this.makeKey(key);
    this.log("setItem", {
      key,
      fullKey,
      userId: this.activeUserId,
      storage: storage === localStorage ? "localStorage" : "sessionStorage",
      valueLength: value.length
    });
    storage.setItem(fullKey, value);
  };

  getItem = (key: string, storage = this.useStorage): string | null => {
    // Auto-initialize if not already done
    if (!this._isInitialized) {
      this.log("Auto-initializing storage on first getItem call", { key });
      this.initialize();
    }

    if (!this.activeUserId) {
      // Try to get from temporary sessionStorage for pre-auth operations
      this.log("No active user - checking temporary sessionStorage", { key });
      return this.getItemNoUser(key);
    }
    const fullKey = this.makeKey(key);
    const value = storage.getItem(fullKey);
    this.log("getItem", {
      key,
      fullKey,
      userId: this.activeUserId,
      storage: storage === localStorage ? "localStorage" : "sessionStorage",
      hasValue: !!value,
      valueLength: value?.length
    });
    return value;
  };

  getItemParsed = <T = ParseResultUnknown>(
    key: string,
    storage = this.useStorage
  ): T | null => {
    // Auto-initialize if not already done
    if (!this._isInitialized) {
      this.log("Auto-initializing storage on first getItemParsed call", {
        key
      });
      this.initialize();
    }

    // Use regular getItem which handles the pre-auth case
    const value = this.getItem(key, storage);
    if (!value) {
      return null;
    }
    try {
      return JSON.parse(value) as T;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      reportErrorSentry(new Error(`Failed to parse item, ${key}`));
      return null;
    }
  };

  removeItem = (key: string, storage = this.useStorage): void => {
    // Auto-initialize if not already done
    if (!this._isInitialized) {
      this.log("Auto-initializing storage on first removeItem call", { key });
      this.initialize();
    }

    if (!this.activeUserId) {
      // Remove from temporary sessionStorage for pre-auth operations
      const tempKey = `9am-temp-${key}`;
      this.log("No active user - removing from temporary sessionStorage", {
        key,
        tempKey
      });
      sessionStorage.removeItem(tempKey);
      return;
    }
    storage.removeItem(this.makeKey(key));
  };

  clear = (storage = this.useStorage): void => {
    storage.clear();
  };

  /**
   * Clears all storage data for the currently active user.
   * This includes all user-specific keys and storage type preferences.
   */
  clearUser = (): void => {
    const clearUserId = this.activeUserId;
    if (!clearUserId) {
      this.log(
        "clearUser - no active user, clearing temporary sessionStorage only"
      );
      // Clear any temporary storage items from sessionStorage
      const keys = Object.keys(sessionStorage);
      const tempKeysToRemove = keys.filter((k) => k.startsWith("9am-temp-"));
      tempKeysToRemove.forEach((k) => sessionStorage.removeItem(k));
      return;
    }

    this.log("clearUser - starting", {
      userId: clearUserId,
      storage:
        this.useStorage === localStorage ? "localStorage" : "sessionStorage"
    });

    const keys = Object.keys(this.useStorage);
    const keysToRemove = keys.filter((k) =>
      k.includes(clearUserId.replace(/-/g, ""))
    );

    this.log("clearUser - removing keys", {
      totalKeys: keys.length,
      keysToRemove: keysToRemove.length,
      keys: keysToRemove
    });

    keysToRemove.forEach((k) => this.useStorage.removeItem(k));
    sessionStorage.removeItem(StorageBloc.keys.useStorageType);

    // Clear the active user ID
    this.setActiveUserId(null);
    this.log("clearUser - completed");
  };

  /**
   * Removes all 9am-* keys from BOTH localStorage and sessionStorage and clears storage type.
   * Use to guarantee a clean slate prior to external token exchanges.
   */
  clearAllUsers = (): void => {
    this.log("clearAllUsers - starting");
    const removeMatching = (storage: Storage) => {
      const keys = Object.keys(storage);
      const keysToRemove = keys.filter((k) => k.startsWith("9am-"));
      keysToRemove.forEach((k) => storage.removeItem(k));
    };

    removeMatching(localStorage);
    removeMatching(sessionStorage);
    sessionStorage.removeItem(StorageBloc.keys.useStorageType);
    this.setActiveUserId(null);
    this.log("clearAllUsers - completed");
  };
}

export const StorageController = new StorageBloc();
